// @noflow
import {
  PreWizardGuestIterableUpsertWithRecaptchaMutation,
  PreWizardGuestIterableUpsertWithRecaptchaMutationVariables
} from '@/gql/graphql'
import { ApolloError, useMutation } from '@apollo/client'
import React, { ChangeEvent, useCallback, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'

import WhiteCheckmarkBlueBg from 'assets/images/icons/checkmarks/white-checkmark-blue-bg.svg'
import BlueCross from 'assets/images/icons/crosses/blue-cross.svg'

import withApollo from '@/components/apollo/withApollo'
import { Button } from '@/components/shared/elements/Button/Button'
import Input from '@/components/shared/elements/Input'

import { PRE_WIZARD_GUEST_ITERABLE_UPSERT_WITH_RECAPTCHA_MUTATION } from './mutations/PreWizardGuestIterableUpsertWithRecaptchaMutation'

const namespace = 'shared'
const copyContext = 'footer_lead_capture'
const EMAIL_INITIAL_STATE = {
  messageKey: null,
  value: ''
}
type EmailState = {
  messageKey: string | null
  value: string
}

const useStandardInputField = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const formRef = useRef<HTMLFormElement>(null)

  const [success, setSuccess] = useState(false)
  const [email, setEmail] = useState<EmailState>(EMAIL_INITIAL_STATE)

  const handleSubmitEmailError = useCallback(
    ({ graphQLErrors }: ApolloError) => {
      const error = graphQLErrors[0]
      const errorClass: string = (error.message || '').split('::').pop() || ''

      const errorMessages: { [key: string]: string } = {
        EmailAlreadySubscribedError: 'already_exists_error',
        EmailInvalidError: 'validation_error',
        RecaptchaInvalidError: 'recaptcha_error'
      }

      const errorKey = errorMessages[errorClass] || 'generic_error'

      setEmail({
        value: '',
        messageKey: errorKey
      })
    },
    []
  )

  const [submitEmailToIterable, { loading }] = useMutation<
    PreWizardGuestIterableUpsertWithRecaptchaMutation,
    PreWizardGuestIterableUpsertWithRecaptchaMutationVariables
  >(PRE_WIZARD_GUEST_ITERABLE_UPSERT_WITH_RECAPTCHA_MUTATION, {
    onError: handleSubmitEmailError,
    onCompleted() {
      setSuccess(true)
      setEmail({
        value: '',
        messageKey: 'success_message'
      })
    }
  })

  const handleOnSubmit = useCallback(async () => {
    if (!formRef.current) return
    if (!formRef.current.reportValidity()) return
    if (!executeRecaptcha) return

    const recaptchaToken = await executeRecaptcha('lead_capture')

    submitEmailToIterable({
      variables: { email: email.value, recaptchaToken }
    })
  }, [email.value, submitEmailToIterable, executeRecaptcha])

  const handleOnChange = useCallback(
    ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>): void => {
      setSuccess(false)

      setEmail({ value, messageKey: null })
    },
    []
  )

  return {
    formRef,
    email,
    loading,
    success,
    handleOnChange,
    handleOnSubmit
  }
}

const LeadCaptureFormMessage = ({
  messageKey,
  success
}: {
  messageKey: string | null
  success: boolean
}) => {
  const { t } = useTranslation(namespace)

  if (!messageKey) return null

  return (
    <p className="footer-lead-capture__message text-regular-14">
      <img alt="checkmark" src={success ? WhiteCheckmarkBlueBg : BlueCross} />
      {t(`${copyContext}.email.${messageKey}`)}
    </p>
  )
}

const LeadCaptureForm = () => {
  const { t } = useTranslation(namespace)

  const { formRef, email, loading, success, handleOnChange, handleOnSubmit } =
    useStandardInputField()

  return (
    <form className="footer-lead-capture__form-inner" ref={formRef}>
      <div className="input-wrapper">
        <Input
          inputId="email"
          className="email"
          name="email"
          type="email"
          autoComplete="off"
          onChange={handleOnChange}
          onBlur={handleOnChange}
          value={email.value}
          placeholder={t(`${copyContext}.email.placeholder`)}
          label=""
          disabled={false}
          invalid={false}
          invalidityMessage=""
          required
        />
        <LeadCaptureFormMessage
          messageKey={email.messageKey}
          success={success}
        />
      </div>
      <Button
        onClick={handleOnSubmit}
        disabled={loading}
        identifier="Footer Lead Capture"
        size="large"
        typography={{
          text: `${copyContext}.button_cta`,
          namespace
        }}
      />
      <div className="footer-lead-capture__message-wrapper__mobile">
        <LeadCaptureFormMessage
          messageKey={email.messageKey}
          success={success}
        />
      </div>
    </form>
  )
}

const FooterLeadCapture = ({
  recaptchaSiteKey
}: {
  recaptchaSiteKey: string
}) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <LeadCaptureForm />
    </GoogleReCaptchaProvider>
  )
}

const initFooterLeadCapture = (): void => {
  const element = document.getElementById('footer-lead-capture-form')

  if (!element) return

  const recaptchaSiteKey = element.dataset.recaptchaSiteKey

  if (!recaptchaSiteKey) return

  const FooterLeadCaptureComponent = withApollo(FooterLeadCapture)

  ReactDOM.render(
    <FooterLeadCaptureComponent recaptchaSiteKey={recaptchaSiteKey} />,
    element
  )
}

export { initFooterLeadCapture }
